<template>
  <div class="ccc-registrations">
    <h5>CCC Diagnostic Registrations</h5>
    <p class="text-muted">View and manage CCC Diagnostic registrations for this location</p>

    <!-- Existing Registrations -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <strong>Existing Registrations</strong>
        <button class="btn btn-sm btn-outline-primary" @click="loadRegistrations" :disabled="loading.registrations">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading.registrations }"></i> Refresh
        </button>
      </div>
      <div class="card-body">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>App</th>
              <th>Registration Key</th>
              <th>Status</th>
              <th v-if="!isProduction">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="registrations.length === 0">
              <td :colspan="isProduction ? 3 : 4" class="text-center text-muted">No registrations found</td>
            </tr>
            <tr v-for="reg in registrations" :key="reg.CCCRegistration">
              <td>{{ reg.App }}</td>
              <td>
                <span :class="{ 'badge bg-warning text-dark': isTestKey(reg.Key) }">
                  {{ reg.Key }}
                </span>
              </td>
              <td>
                <span :class="reg.IsInactive ? 'badge bg-secondary' : 'badge bg-success'">
                  {{ reg.IsInactive ? 'Inactive' : 'Active' }}
                </span>
              </td>
              <td v-if="!isProduction">
                <button
                  v-if="isTestKey(reg.Key)"
                  class="btn btn-sm btn-danger"
                  @click="confirmDelete(reg)"
                  :disabled="loading.deleting"
                >
                  <i class="fas fa-trash"></i> Remove
                </button>
                <span v-else class="text-muted small">Production Key</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add Test Registration (only in non-production) -->
    <div v-if="!isProduction" class="card">
      <div class="card-header">
        <strong>Add Test Registration</strong>
      </div>
      <div class="card-body">
        <p class="text-muted">Add test registration keys for apps that don't have existing registrations</p>

        <div class="row">
          <div class="col-md-6 mb-3">
            <div class="card">
              <div class="card-body">
                <h6>ADAS Map</h6>
                <p class="small text-muted">CCC Diagnostic registration for ADAS Map</p>
                <button
                  v-if="!hasRegistration('ADAS')"
                  class="btn btn-sm btn-primary"
                  @click="addTestRegistration('ADAS')"
                  :disabled="loading.registering"
                >
                  <span v-if="loading.registering" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="fas fa-plus"></i>
                  Add Test ADAS Registration
                </button>
                <span v-else class="badge bg-success">
                  <i class="fas fa-check"></i> Registration Exists
                </span>
              </div>
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <div class="card">
              <div class="card-body">
                <h6>Mobile Copilot</h6>
                <p class="small text-muted">CCC Diagnostic registration for Mobile Copilot</p>
                <button
                  v-if="!hasRegistration('MCP')"
                  class="btn btn-sm btn-primary"
                  @click="addTestRegistration('MCP')"
                  :disabled="loading.registering"
                >
                  <span v-if="loading.registering" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="fas fa-plus"></i>
                  Add Test MCP Registration
                </button>
                <span v-else class="badge bg-success">
                  <i class="fas fa-check"></i> Registration Exists
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="registrationMessage" :class="`alert ${registrationMessage.type} mt-3`" role="alert">
          {{ registrationMessage.text }}
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Deletion</h5>
            <button type="button" class="btn-close" @click="showDeleteConfirm = false"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to remove this test registration key?</p>
            <p class="text-muted"><strong>Key:</strong> {{ regToDelete?.Key }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteConfirm = false">
              Cancel
            </button>
            <button type="button" class="btn btn-danger" @click="performDeleteRegistration">
              <i class="fas fa-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { cccRegistrationStore } from '@/stores/cccRegistration'
import { contactStore } from '@/stores/contact'
import { locationStore } from '@/stores/location'

export default {
  name: 'CccRegistrations',
  props: {
    firm: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      registrationMessage: null,
      showDeleteConfirm: false,
      regToDelete: null
    }
  },
  computed: {
    ...mapState(cccRegistrationStore, ['registrations', 'loading']),
    ...mapState(contactStore, ['addresses']),
    ...mapState(locationStore, ['location']),
    isProduction() {
      const env = window.config ? window.config.environment : 'prod'
      return env === 'prod'
    },
    primaryAddress() {
      // Get the primary address or the first address
      if (this.addresses && this.addresses.length > 0) {
        const primary = this.addresses.find(addr => addr.isPrimary)
        return primary || this.addresses[0]
      }
      return null
    }
  },
  methods: {
    ...mapActions(cccRegistrationStore, [
      'getRegistrations',
      'deleteRegistration',
      'testRegisterADAS',
      'testRegisterMCP'
    ]),
    ...mapActions(contactStore, ['getAddresses']),
    async loadRegistrations() {
      if (this.firm && this.firm.id) {
        await this.getRegistrations(this.firm.id)
      }
    },
    async loadContactInfo() {
      if (this.firm && this.firm.contact) {
        await this.getAddresses(this.firm.contact)
      }
    },
    hasRegistration(appType) {
      return this.registrations.some(reg => reg.App === appType)
    },
    isTestKey(key) {
      return key && key.startsWith('TESTKEY-')
    },
    generateTestKey(appType) {
      const timestamp = Date.now()
      const cccNumber = this.location?.cccNumber || '00000'
      return `TESTKEY-${appType}-${cccNumber}-${timestamp}`
    },
    async addTestRegistration(appType) {
      this.registrationMessage = null

      const registrationData = {
        registrationKey: this.generateTestKey(appType),
        cccLicenseNumber: this.location?.cccNumber || '',
        status: 'Active',
        shopName: this.location?.name || this.firm.name,
        streetAddress: this.primaryAddress?.street || 'Test Address',
        city: this.primaryAddress?.city || 'Test City',
        state: this.primaryAddress?.state || 'CA',
        zipcode: parseInt(this.primaryAddress?.postalCode) || 90000
      }

      let result
      if (appType === 'ADAS') {
        result = await this.testRegisterADAS(registrationData)
      } else {
        result = await this.testRegisterMCP(registrationData)
      }

      if (result.success) {
        this.registrationMessage = {
          type: 'alert-success',
          text: `Successfully added test ${appType} registration!`
        }
        await this.loadRegistrations()
      } else {
        this.registrationMessage = {
          type: 'alert-danger',
          text: `Registration failed: ${result.error || 'Unknown error'}`
        }
      }
    },
    confirmDelete(registration) {
      this.regToDelete = registration
      this.showDeleteConfirm = true
    },
    async performDeleteRegistration() {
      if (!this.regToDelete) return

      const result = await this.deleteRegistration(this.regToDelete.CCCRegistration)
      this.showDeleteConfirm = false

      if (result.success) {
        this.registrationMessage = {
          type: 'alert-success',
          text: 'Test registration key deleted successfully!'
        }
        await this.loadRegistrations()
      } else {
        this.registrationMessage = {
          type: 'alert-danger',
          text: `Deletion failed: ${result.error || 'Unknown error'}`
        }
      }

      this.regToDelete = null
    }
  },
  async mounted() {
    await this.loadContactInfo()
    await this.loadRegistrations()
  }
}
</script>

<style scoped>
.ccc-registrations {
  padding: 20px;
}
</style>
